import React, { useRef, useState } from "react";
import {
  FiPhone,
  FiMail,
  FiMapPin,
  FiBriefcase,
  FiUser,
  FiHome,
  FiBookOpen,
  FiUsers,
} from "react-icons/fi";
import { FiCamera } from "react-icons/fi";
import axios from "axios";

import authManager from "../../utils/authManager";
import Layout from "../../components/user/Layout";
import { formatDate, getFileUrl } from "../../utils/helper";

const Profile = () => {
  // 🔹 user ko state me rakha
  const [user, setUser] = useState(authManager.getUserProfile());
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  if (!user) return null;

  // 🔹 image click
  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  // 🔹 image upload
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      setUploading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}member/upload-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authManager.getAccessToken()}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const uploadedImage = res.data?.image;

      // 🔹 UI update
      const updatedUser = { ...user, image: uploadedImage };
      setUser(updatedUser);

      // 🔹 local storage / auth update
      authManager.setUserProfile(updatedUser);
    } catch (err) {
      alert("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const renderSection = (title, icon, items) => {
    const validItems = items.filter(
      (i) => i.value !== null && i.value !== undefined && i.value !== ""
    );
    if (validItems.length === 0) return null;

    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
            {icon}
          </div>
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
        </div>

        <div className="space-y-3">
          {validItems.map((item, idx) => (
            <div
              key={idx}
              className="flex justify-between items-start gap-4 px-4 py-3 rounded-lg bg-gray-50 border border-gray-100"
            >
              <p className="text-sm text-gray-500">{item.label}</p>
              <p className="text-sm font-medium text-gray-800 text-right break-words">
                {item.value}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-5 px-4">
        <div className="max-w-6xl mx-auto space-y-8">

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div
              className="h-10"
              style={{
                background: `linear-gradient(90deg, ${
                  user.gradient_color || "#6366F1"
                }, #4F46E5)`,
              }}
            />

            <div className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6 -mt-14">
              <div className="flex items-center gap-4">

                {/* 🔹 Hidden input */}
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  className="hidden"
                />

                {/* 🔹 IMAGE (UI SAME + CLICKABLE) */}
                <div
                  onClick={handleImageClick}
                  className="relative cursor-pointer group"
                >
                  {user.image ? (
                    <img
                      src={getFileUrl(user.image, "members")}
                      alt="profile"
                      className="w-28 h-28 rounded-xl object-cover border-4 border-white"
                    />
                  ) : (
                    <div className="w-28 h-28 rounded-xl bg-indigo-600 text-white flex items-center justify-center text-3xl font-bold">
                      {user.name?.charAt(0)}
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/40 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                    {uploading ? (
                      <span className="text-white text-xs">Uploading...</span>
                    ) : (
                      <FiCamera className="text-white text-2xl" />
                    )}
                  </div>
                </div>

                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    {user.name}
                  </h1>
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <FiMail className="text-gray-400" />
                    {user.email}
                  </p>
                </div>
              </div>

              <span
                className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                  user.status === "Active"
                    ? "bg-green-50 text-green-700"
                    : "bg-red-50 text-red-600"
                }`}
              >
                ● {user.status}
              </span>
            </div>
          </div>

          {/* ================= INFO GRID ================= */}
          <div className="grid md:grid-cols-2 gap-6">

            {renderSection("Contact Details", <FiPhone />, [
              { label: "Mobile", value: user.contact_no },
              { label: "Alternate Mobile", value: user.contact_no_two },
              { label: "Address", value: user.address },
            ])}

            {renderSection("Professional Details", <FiBriefcase />, [
              { label: "Department", value: user.department },
              { label: "Union Code", value: user.union_code },
              { label: "Date of Joining", value: formatDate(user.doj) },
            ])}

            {renderSection("Personal Details", <FiUser />, [
              { label: "Father Name", value: user.father_name },
              { label: "Date of Birth", value: formatDate(user.dob) },
              { label: "Blood Group", value: user.blood_group },
            ])}

            {renderSection("Location Details", <FiMapPin />, [
              { label: "State", value: user.state },
              { label: "District", value: user.district },
              { label: "City", value: user.city },
              { label: "Village", value: user.village },
            ])}

            {renderSection("Education & Skills", <FiBookOpen />, [
              { label: "Education Qualification", value: user.education_qualification },
              { label: "Technical Qualification", value: user.technical_qualification },
              { label: "Income", value: user.income },
            ])}

            {renderSection("Native Details", <FiHome />, [
              { label: "Native Address", value: user.native_place_address },
              { label: "Native Village", value: user.native_village },
              { label: "Native Taluka", value: user.native_taluka },
              { label: "Native District", value: user.native_district },
              { label: "Native Mobile", value: user.native_mobile },
            ])}

          </div>

          {/* ================= FAMILY ================= */}
          {Array.isArray(user.sons) && user.sons.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-800 mb-5 flex items-center gap-2">
                <FiUsers className="text-indigo-500" />
                Family Members
              </h2>
              <div className="flex gap-5 overflow-x-auto">
                {user.sons.map((son) => (
                  <div
                    key={son.id}
                    className="w-48 flex-shrink-0 rounded-xl border border-gray-200 p-4 bg-gray-50"
                  >
                    {son.document ? (
                      <img
                        src={getFileUrl(son.document, "sons")}
                        alt={son.name}
                        className="w-14 h-14 rounded-full mx-auto mb-3"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-indigo-600 text-white flex items-center justify-center mx-auto mb-3">
                        {son.name?.charAt(0)}
                      </div>
                    )}
                    <p className="text-center text-sm font-medium text-gray-800">
                      {son.name}
                    </p>
                    <p className="text-center text-xs text-gray-500">
                      Aadhar: {son.aadhar_no}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
};

export default Profile;
